import { useLiveQuery } from "dexie-react-hooks";
import { ExpenseKind, formatUsd, newId, type Expense } from "@haulsheet/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { TextInput } from "../components/ui/Field";
import { getDb } from "../db/dexie";
import { formatPrice, fuelPatch, localDateIso, parseFuelForm } from "../logs/fuel";
import { useAuthStore } from "../store/authStore";
import { useUnsavedGuard } from "../pwa/unsaved";
import { getSyncClient } from "../sync/SyncClient";

interface FormState {
  gallons: string;
  price: string;
  occurredOn: string;
  where: string;
}

function blank(tz?: string | null): FormState {
  return { gallons: "", price: "", occurredOn: localDateIso(tz), where: "" };
}

function fromRow(r: Expense): FormState {
  return {
    gallons: r.gallons != null ? String(r.gallons) : "",
    price: formatPrice(r.priceMilliCents),
    occurredOn: r.occurredOn,
    where: r.note ?? "",
  };
}

export function LogsFuelScreen() {
  const me = useAuthStore((s) => s.me);
  const tz = me?.org.timezone;
  const [form, setForm] = useState<FormState>(() => blank(tz));
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useUnsavedGuard("logs-fuel", editingId != null || [form.gallons, form.price, form.where].some((v) => (v ?? "").trim() !== ""));
  const rows = useLiveQuery(async () => {
    const list = await getDb()
      .expense.filter((e) => e.kind === ExpenseKind.fuel && e.deletedAt == null)
      .toArray();
    return list.sort(
      (a, b) =>
        b.occurredOn.localeCompare(a.occurredOn) || b.createdAt.localeCompare(a.createdAt),
    );
  }, []);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  function reset() {
    setForm(blank(tz));
    setEditingId(null);
    setConfirmDelete(false);
    setError(null);
  }

  function startEdit(r: Expense) {
    setForm(fromRow(r));
    setEditingId(r.id);
    setConfirmDelete(false);
    setError(null);
  }

  async function save() {
    if (!me) return;
    const parsed = parseFuelForm(form);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    const now = new Date().toISOString();
    const client = getSyncClient();
    if (editingId) {
      const row = await getDb().expense.get(editingId);
      if (!row || row.deletedAt != null) {
        setError("That fill-up was deleted on another device");
        setEditingId(null);
        return;
      }
      const patch = fuelPatch(row, parsed.value);
      if (Object.keys(patch).length > 0) {
        await client.mutate({
          entity: "expense",
          op: "upsert",
          id: row.id,
          baseVersion: row.version || null,
          patch,
          row: { ...row, ...patch, updatedAt: now },
        });
      }
    } else {
      const id = newId();
      await client.mutate({
        entity: "expense",
        op: "upsert",
        id,
        baseVersion: null,
        row: {
          id,
          orgId: me.org.id,
          version: 0,
          createdAt: now,
          updatedAt: now,
          deletedAt: null,
          driverId: me.driverId,
          kind: ExpenseKind.fuel,
          ...parsed.value,
          loadId: null,
        },
      });
    }
    reset();
  }

  async function remove() {
    if (!editingId) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    const row = await getDb().expense.get(editingId);
    if (row) {
      await getSyncClient().mutate({
        entity: "expense",
        op: "delete",
        id: row.id,
        baseVersion: row.version || null,
      });
    }
    reset();
  }

  const month = localDateIso(tz).slice(0, 7);
  const monthRows = (rows ?? []).filter((r) => r.occurredOn.startsWith(month));
  const monthGallons = monthRows.reduce((s, r) => s + (r.gallons ?? 0), 0);
  const monthCents = monthRows.reduce((s, r) => s + r.amountCents, 0);

  return (
    <div className="stack">
      <h1 className="screen-title">Fuel log</h1>
      <div className="row">
        <Link to="/logs/detention" className="chip">
          Detention
        </Link>
        <Link to="/logs/maintenance" className="chip">
          Maintenance
        </Link>
      </div>
      <form
        className="card stack"
        aria-label={editingId ? "Edit fill-up" : "Add fill-up"}
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {editingId ? <strong>Edit fill-up</strong> : null}
        <TextInput
          label="Gallons"
          placeholder="0.0"
          inputMode="decimal"
          value={form.gallons}
          onChange={set("gallons")}
        />
        <TextInput
          label="Price $/gal"
          placeholder="0.000"
          inputMode="decimal"
          value={form.price}
          onChange={set("price")}
        />
        <TextInput label="Date" type="date" value={form.occurredOn} onChange={set("occurredOn")} />
        <TextInput
          label="Where (optional)"
          placeholder="Love's, Cortez CO"
          value={form.where}
          onChange={set("where")}
        />
        {error ? (
          <p className="legal-flag" role="alert" style={{ margin: 0 }}>
            {error}
          </p>
        ) : null}
        <div className="row">
          <Button variant="primary" type="submit">
            {editingId ? "Save changes" : "Add fuel"}
          </Button>
          {editingId ? (
            <>
              <Button variant="ghost" onClick={reset}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => void remove()}>
                {confirmDelete ? "Tap again to delete" : "Delete"}
              </Button>
            </>
          ) : null}
        </div>
      </form>
      {monthRows.length > 0 ? (
        <p className="muted" data-testid="fuel-month" style={{ margin: 0 }}>
          This month: {Math.round(monthGallons * 10) / 10} gal · {formatUsd(monthCents)}
        </p>
      ) : null}
      <div className="card" style={{ padding: 0 }}>
        {(rows ?? []).length === 0 ? (
          <p className="muted" style={{ padding: "1rem" }}>
            No fuel logged yet
          </p>
        ) : (
          (rows ?? []).map((r) => (
            <div key={r.id} className="list-item" data-testid="fuel-row">
              <div>
                <div>
                  {r.occurredOn}
                  {r.note ? ` · ${r.note}` : ""}
                </div>
                <div className="muted">
                  {r.gallons ?? "—"} gal
                  {r.priceMilliCents != null ? ` @ $${formatPrice(r.priceMilliCents)}` : ""}
                </div>
              </div>
              <div className="row" style={{ alignItems: "center" }}>
                <span>{formatUsd(r.amountCents)}</span>
                <Button
                  variant="ghost"
                  aria-label={`Edit fill-up ${r.occurredOn} ${r.gallons ?? ""} gal`}
                  onClick={() => startEdit(r)}
                >
                  Edit
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
